import { ListFilter, ListFilterOperator, SortDirection } from "@common/api/filterTypes";
import { EntityType } from "@common/Common.types";
import { Color } from "@dotlegal/dotlegal-ui-components";
import { TranslationStringType } from "@localization/LocalizationContext";
import { SegmentedControlOption } from "@common/components/segmentedControl/SegmentedControl";
import { FrameworkControlStatus } from "../../framework/frameworks/Frameworks.types";
import { RiskLevelIndicator } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";

// The Operational tab holds one table per assessment level. An area assessed at both levels shows a switcher
// rather than both tables at once, so the two never compete for the same screen.
export enum RiskOperationalSection {
    Entities = 0,
    Organisation = 1,
}

export function buildRiskOperationalSectionViews(translateString: TranslationStringType): Array<SegmentedControlOption<RiskOperationalSection>> {
    return [
        { label: translateString("entities"), value: RiskOperationalSection.Entities },
        { label: translateString("organization"), value: RiskOperationalSection.Organisation },
    ];
}

export interface RiskChip {
    name: string;
    color?: Color;
}

// Normalized status shared across entity types. Mirrors the backend OperationalEntityStatus enum; the backend
// maps each value onto the per-type native status (see RiskOperationalSqlQueryBuilder).
export enum OperationalEntityStatus {
    Draft = 1,
    Active = 2,
    Archived = 3,
}

// The single source for how a normalized status reads, so the status filter's options and the Entity status
// column can never label the same status differently.
export const operationalEntityStatusTranslationKey: Record<OperationalEntityStatus, string> = {
    [OperationalEntityStatus.Draft]: "draft",
    [OperationalEntityStatus.Active]: "active",
    [OperationalEntityStatus.Archived]: "archived",
};

export const allOperationalEntityStatuses: Array<OperationalEntityStatus> = [
    OperationalEntityStatus.Draft,
    OperationalEntityStatus.Active,
    OperationalEntityStatus.Archived,
];

// Table/display column ids (used for table `property`, visible-column storage, and TableDisplayFields).
export enum RiskOperationalColumn {
    Type = "type",
    Name = "name",
    EntityStatus = "entityStatus",
    Tags = "tags",
    BusinessArea = "businessArea",
    Responsible = "responsible",
    Risk = "risk",
    AfterMitigation = "afterMitigation",
    Mitigation = "mitigation",
    RiskAcceptance = "riskAcceptance",
    Avoidance = "avoidance",
    ControlStatus = "controlStatus",
    ControlCount = "controlCount",
    EstimatedLoss = "estimatedLoss",
    EstimatedCost = "estimatedCost",
    ExposureAfterMitigation = "exposureAfterMitigation",
    LinkedRisk = "linkedRisk",
}

// Mirrors the backend RiskOperationalColumn enum (numeric) — used in the search request payload.
export enum RiskColumn {
    Type = 0,
    Name = 1,
    Tags = 2,
    BusinessArea = 3,
    Responsible = 4,
    Risk = 5,
    AfterMitigation = 6,
    Mitigation = 7,
    RiskAcceptance = 8,
    Avoidance = 9,
    ControlStatus = 10,
    ControlCount = 11,
    EstimatedLoss = 12,
    EstimatedCost = 13,
    ExposureAfterMitigation = 14,
    LinkedRisk = 15,
    EntityStatus = 16,
}

export const riskColumnToBackend: Record<RiskOperationalColumn, RiskColumn> = {
    [RiskOperationalColumn.Type]: RiskColumn.Type,
    [RiskOperationalColumn.Name]: RiskColumn.Name,
    [RiskOperationalColumn.EntityStatus]: RiskColumn.EntityStatus,
    [RiskOperationalColumn.Tags]: RiskColumn.Tags,
    [RiskOperationalColumn.BusinessArea]: RiskColumn.BusinessArea,
    [RiskOperationalColumn.Responsible]: RiskColumn.Responsible,
    [RiskOperationalColumn.Risk]: RiskColumn.Risk,
    [RiskOperationalColumn.AfterMitigation]: RiskColumn.AfterMitigation,
    [RiskOperationalColumn.Mitigation]: RiskColumn.Mitigation,
    [RiskOperationalColumn.RiskAcceptance]: RiskColumn.RiskAcceptance,
    [RiskOperationalColumn.Avoidance]: RiskColumn.Avoidance,
    [RiskOperationalColumn.ControlStatus]: RiskColumn.ControlStatus,
    [RiskOperationalColumn.ControlCount]: RiskColumn.ControlCount,
    [RiskOperationalColumn.EstimatedLoss]: RiskColumn.EstimatedLoss,
    [RiskOperationalColumn.EstimatedCost]: RiskColumn.EstimatedCost,
    [RiskOperationalColumn.ExposureAfterMitigation]: RiskColumn.ExposureAfterMitigation,
    [RiskOperationalColumn.LinkedRisk]: RiskColumn.LinkedRisk,
};

// Every selectable column, including the two opt-in control columns. Used to validate stored column ids.
export const allRiskOperationalColumns: Array<RiskOperationalColumn> = [
    RiskOperationalColumn.Type,
    RiskOperationalColumn.Name,
    RiskOperationalColumn.EntityStatus,
    RiskOperationalColumn.BusinessArea,
    RiskOperationalColumn.Responsible,
    RiskOperationalColumn.Tags,
    RiskOperationalColumn.Risk,
    RiskOperationalColumn.AfterMitigation,
    RiskOperationalColumn.LinkedRisk,
    RiskOperationalColumn.Mitigation,
    RiskOperationalColumn.RiskAcceptance,
    RiskOperationalColumn.Avoidance,
    RiskOperationalColumn.ControlCount,
    RiskOperationalColumn.ControlStatus,
    RiskOperationalColumn.EstimatedLoss,
    RiskOperationalColumn.EstimatedCost,
    RiskOperationalColumn.ExposureAfterMitigation,
];

// The entity's worst-case current risk in the area (max across its scored assessments); carries scale + label
// so the chip colours match the matrix. Mirrors the backend HighestRisk.
export interface HighestRisk {
    risk: number;
    riskLabel: string;
    scale: number;
}

export interface RiskOperationalFieldOrderItem {
    column: RiskColumn;
    displayOrder: number;
}

export interface RiskOperationalDisplayFieldRequest {
    selectedFields: Array<RiskOperationalFieldOrderItem>;
}

export interface RiskOperationalFilter {
    riskAreaId: string;
    showOnlyMine?: boolean;
    types?: ListFilter<EntityType>;
    statuses?: ListFilter<OperationalEntityStatus>;
    // Ja/Nej: the entity is plotted on the governance matrix (a live assessment with a scored current revision).
    // Nej is the governance Assessed tile's gap ("not assessed"), which deep-links here.
    isAssessed?: boolean;
    entityIds?: ListFilter<string>;
    responsibleIds?: ListFilter<string>;
    businessAreaIds?: ListFilter<string>;
    tagIds?: ListFilter<string>;
    scenarioIds?: ListFilter<string>;
    riskLevels?: ListFilter<number>;
    // The same risk-level values as riskLevels, matched against the entity's projected residual instead of its
    // current worst risk. Entities with a blank After mitigation cell survive a NotIn selection.
    afterMitigationLevels?: ListFilter<number>;
    hasMitigation?: boolean;
    overAppetite?: boolean;
    hasRiskAcceptance?: boolean;
    hasAvoidance?: boolean;
    hasControls?: boolean;
    // Ja/Nej: the entity has a live over-appetite risk with no control linked to the risk itself. Ja is the
    // governance coverage tile's red band ("not fully covered"), which deep-links here.
    hasOverAppetiteRiskWithoutControls?: boolean;
    // Ja/Nej: the entity has a live over-appetite risk carrying no treatment decision at all. Ja is the governance
    // over-appetite tile's gap line, which deep-links here.
    hasOverAppetiteRiskWithoutTreatment?: boolean;
    // Ja/Nej: the entity has at least one directly linked entity over the area appetite. Combined with
    // overAppetite = Nej it is the governance linked-risk tile's exact deep-link landing set.
    hasLinkedRiskOverAppetite?: boolean;
    frameworkControlIds?: ListFilter<string>;
    controlStatuses?: ListFilter<number>;
    estimatedLossFrom?: number;
    estimatedLossTo?: number;
    estimatedCostFrom?: number;
    estimatedCostTo?: number;
    exposureAfterMitigationFrom?: number;
    exposureAfterMitigationTo?: number;
    searchText?: string;
    pageNumber: number;
    pageSize: number;
    sortByColumn: RiskColumn;
    sortDirection: SortDirection;
}

export interface RiskOperationalTableRequest {
    displayFields: RiskOperationalDisplayFieldRequest;
    filter: RiskOperationalFilter;
}

export interface RiskOperationalExportHeader {
    header: string;
    column: string;
}

// The persisted Saved-View filter shape. Deliberately omits riskAreaId (injected from the route),
// searchText (ad-hoc, not part of a saved view), and pageSize (constant default).
export interface RiskOperationalSavedFilter {
    showOnlyMine?: boolean;
    types?: ListFilter<EntityType>;
    statuses?: ListFilter<OperationalEntityStatus>;
    isAssessed?: boolean;
    entityIds?: ListFilter<string>;
    responsibleIds?: ListFilter<string>;
    businessAreaIds?: ListFilter<string>;
    tagIds?: ListFilter<string>;
    scenarioIds?: ListFilter<string>;
    riskLevels?: ListFilter<number>;
    afterMitigationLevels?: ListFilter<number>;
    hasMitigation?: boolean;
    overAppetite?: boolean;
    hasRiskAcceptance?: boolean;
    hasAvoidance?: boolean;
    hasControls?: boolean;
    hasOverAppetiteRiskWithoutControls?: boolean;
    hasOverAppetiteRiskWithoutTreatment?: boolean;
    hasLinkedRiskOverAppetite?: boolean;
    frameworkControlIds?: ListFilter<string>;
    controlStatuses?: ListFilter<number>;
    estimatedLossFrom?: number;
    estimatedLossTo?: number;
    estimatedCostFrom?: number;
    estimatedCostTo?: number;
    exposureAfterMitigationFrom?: number;
    exposureAfterMitigationTo?: number;
    pageNumber: number;
    sortByColumn: RiskColumn;
    sortDirection: SortDirection;
}

export const defaultRiskFilter: RiskOperationalSavedFilter = {
    showOnlyMine: false,
    statuses: { value: [OperationalEntityStatus.Active, OperationalEntityStatus.Draft], filterOperator: ListFilterOperator.In },
    pageNumber: 1,
    sortByColumn: RiskColumn.Name,
    sortDirection: SortDirection.ASC,
};

export const backendColumnToRisk = Object.fromEntries(Object.entries(riskColumnToBackend).map(([risk, backend]) => [backend, risk])) as Record<
    RiskColumn,
    RiskOperationalColumn
>;

// The entity's shown mitigation-plan roll-up. status is the single plan's status, only meaningful when
// count === 1; with more than one the UI shows a neutral count. Mirrors backend RiskMitigationSummary.
export interface RiskMitigationSummary {
    status: number | null;
    count: number;
}

// A selection key for an operational entity. Entities are identified by type + id (the same id can exist
// across types), so bulk selection is keyed by the pair.
export const entityKey = (type: EntityType, entityId: string) => `${type}:${entityId}`;

// The user-facing label for an assessable entity type. System renders as "asset", so this can't use the
// generic translateEntityType (which keys off the enum name).
export function entityTypeLabel(type: EntityType, translateString: (key: string) => string): string {
    switch (type) {
        case EntityType.ProcessingActivity:
            return translateString("processingActivity");
        case EntityType.System:
            return translateString("asset");
        case EntityType.LegalEntity:
            return translateString("legalEntity");
        case EntityType.Process:
            return translateString("process");
        default:
            return "";
    }
}

export interface RiskOperationalItem {
    type: EntityType;
    entityId: string;
    name: string;
    // The entity's own lifecycle, normalized across the types by the same map the status filter uses.
    // Null for a native status with no normalized equivalent.
    entityStatus: OperationalEntityStatus | null;
    responsibleId: string | null;
    tagIds: Array<string>;
    businessAreaIds: Array<string>;
    highestRisk: HighestRisk | null;
    afterMitigation: HighestRisk | null;
    // Highest current risk among the entity's directly linked entities in this area (the entity graph's edge
    // set). Null when no linked entity carries a scored assessment. Mirrors the backend RiskOperationalItem.
    linkedRisk: HighestRisk | null;
    mitigation: RiskMitigationSummary | null;
    riskAcceptanceCount: number;
    avoidanceCount: number;
    // The distinct framework controls linked across the entity's active assessments and their rolled-up status
    // (any Overdue wins). controlStatus is null when no controls are linked. Mirrors the backend RiskOperationalItem.
    linkedControlIds: Array<string>;
    controlStatus: FrameworkControlStatus | null;
    // Per-entity exposure roll-ups: summed across the entity's active assessments in Financial mode, averaged in
    // Scale mode; estimated cost is always summed. Null when exposure is not configured or nothing carries a
    // stamped value. Formatted against the result's exposure mode/currency/bound.
    estimatedLoss: number | null;
    estimatedCost: number | null;
    exposureAfterMitigation: number | null;
}

// Mirrors the backend Core.Filtering.Risk.RiskExposureSummary — the aggregated exposure picture for the
// filtered view. Sums apply in Financial mode; Scale mode reads the averages (ordinal values are never summed).
export interface RiskExposureSummary {
    assessedCount: number;
    withExposureCount: number;
    grossSum: number | null;
    grossAverage: number | null;
    afterMitigationSum: number | null;
    afterMitigationAverage: number | null;
    // Assessments whose active plan carries a stamped expected exposure. Zero means no active plans move the
    // after-mitigation figure, so the "→ efter mitigering" display is hidden rather than repeating gross.
    afterMitigationPlanCount: number;
    mitigationCostSum: number | null;
}

export interface RiskOperationalSearchResult {
    items: Array<RiskOperationalItem>;
    totalCount: number;
    totalPages: number;
    riskLevels: Array<string>;
    scale: number;
    appetite: number;
    riskLevelIndicators: Array<RiskLevelIndicator>;
    // Null when the customer has not configured exposure (None mode) — the banner is hidden entirely.
    exposure: RiskExposureSummary | null;
    exposureMode: RiskExposureMode;
    exposureCurrencyCode: string | null;
    exposureScaleUpperBound: number | null;
}
